import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import * as CryptoJS from 'crypto-js';
import { CookieUtil } from '@utils/cookies';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { environment } from '@enviroment/environment';

@Injectable({
  providedIn: 'root',
})
export class SimulateInvestmentV2Service {
  private domain: string;
  private endpoint: string = 'simulate-investment/simulate-investment-v2';
  /* private endpoint: string = 'simulate-investment'; */
  private api_key: string;

  constructor(
    private cookies: CookieUtil,
    private http: HttpClient,
    private recaptchaV3: ReCaptchaV3Service,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(DOCUMENT) private document: Document,
  ) {
    /* this.domain = `${environment.API_BASE_URL}${environment.version}`; */
    this.domain = environment.API_BASE_URL;
    this.api_key = environment.API_KEY;
  }

  /**
   * Realiza la simulación de inversión.
   * Si `recaptcha` es true, primero ejecuta el reCAPTCHA antes de la simulación.
   *
   * @param body Datos de la solicitud para la simulación.
   * @param recaptcha Indica si se debe ejecutar el reCAPTCHA.
   * @returns Un Observable con la respuesta de la simulación.
   */
  SIMULATE_INVESTMENT_V2(body: any, recaptcha: boolean = false): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) {
      // En SSR no se puede ejecutar reCAPTCHA ni hacer llamadas directas al DOM
      // Retornamos un Observable que emite un error.
      return new Observable((observer) => {
        observer.error(new Error('La operación no es compatible con Server-Side Rendering.'));
        observer.complete();
      });
    }

    // Clonar el body para no mutar el objeto original
    let requestBody = { ...body };

    const requestUrl = this.buildRequestUrl();
    const headers = this.buildRequestHeaders(recaptcha);

    // Observable que representará la ejecución del reCAPTCHA o un Observable vacío.
    const recaptchaExecution$: Observable<string | undefined> =
      recaptcha && environment.ACTIVE_RECAPTCHA ? this.recaptchaV3.execute('simulation') : of(undefined);

    return recaptchaExecution$.pipe(
      switchMap((token: string | undefined) => {
        if (token && environment.ACTIVE_RECAPTCHA) {
          requestBody.token = token;
        }

        if (!this.cookies.check('ref')) {
          requestBody.ref = this.cookies.get('ref');
        }

        return this.http.post(requestUrl.href, requestBody, { observe: 'response', headers });
      }),
    );
  }

  /**
   * Construye la URL completa para la solicitud, incluyendo parámetros si aplican.
   * @returns URL de la solicitud.
   */
  private buildRequestUrl(): URL {
    const requestUrl = new URL(this.domain + this.endpoint);
    const currentUrl = this.document.location.href;
    const urlParams = new URLSearchParams(currentUrl.split('?')[1]);

    if (
      urlParams.has('rtir') &&
      urlParams.get('rtir') === environment.HAS_UUID_REMOVE_RTIR &&
      environment.ENVIRONMENT_NAME === 'test'
    ) {
      requestUrl.searchParams.set('rtir', environment.HAS_UUID_REMOVE_RTIR);
    }
    return requestUrl;
  }

  /**
   * Construye y retorna las cabeceras HTTP necesarias.
   * @returns HttpHeaders para la solicitud.
   */
  private buildRequestHeaders(recaptcha: boolean): HttpHeaders {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'x-api-key': 'simulate-' + this.api_key,
    });

    if (environment.ACTIVE_RECAPTCHA && recaptcha) {
      headers = headers.set('x-key', this.getEncryptedKey());
    }
    return headers;
  }

  /**
   * Genera la clave encriptada para el encabezado 'x-key'.
   * @returns Clave encriptada.
   */
  private getEncryptedKey(): string {
    const key = environment.SECRET_KEY;
    const currentDate = new Date();
    const formattedDate = currentDate.toISOString().split('T')[0];
    const dataToEncrypt = `${formattedDate}:${key}`;
    const encryptedKey = CryptoJS.HmacSHA256(dataToEncrypt, key).toString(CryptoJS.enc.Hex);
    return encryptedKey;
  }
}
